import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import MemberNoteEditorModal from './MemberNoteEditorModal';
import { memberNoteKey } from '../utils/dashboard/commanderMemberNoteHelpers';
import { ONLINE_TIMEOUT, DEFAULT_OFFLINE_TIMEOUT } from '../utils/dashboard/constants';
import THEMES from './memberTree/memberTreeThemes';
import MemberTreeRow from './memberTree/MemberTreeRow';
import { getMemberExpandKey } from '../utils/dashboard/memberTreeHelpers';
import { canEditMissionBoard, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';

const btnGreen = {
  minWidth: 0,
  background: '#e8f5e9',
  border: '1px solid #a5d6a7',
  color: '#2e7d32',
  cursor: 'pointer'
};
const btnBlue = {
  minWidth: 0,
  background: '#eaf3fb',
  border: '1px solid #b5d0ea',
  color: '#1976d2',
  cursor: 'pointer'
};

/**
 * 指挥页队员树（桌面 / 手机共用）
 */
export default function MemberTreeList({
  variant = 'desktop',
  orgMap = {},
  openOrg = {},
  openGroup = {},
  openMember = {},
  setOpenOrg,
  setOpenGroup,
  setOpenMember,
  setHighlightedMemberIds,
  highlightedMemberName,
  setHighlightedMemberName,
  setActiveMemberId,
  showTracks = {},
  isMemberTrackVisible,
  streamingMembers,
  areAllGroupsExpanded,
  areAllMembersExpanded,
  expandOnlineInOrg,
  setOrgGroupsExpanded,
  expandOnlineInGroup,
  setGroupMembersExpanded,
  flyToMember,
  toggleTrack,
  openLiveStream,
  startRealTimeTrackDrawing,
  copyToClipboard,
  openDownloadTrackModal,
  fetchMemberTrack,
  onMemberIconChange,
  realTimeTrackDrawing,
  stopTrackFeatures,
  hiddenMapMemberIds,
  onToggleMemberMapHidden,
  commandFeatures = {},
  onlineTimeoutMs = ONLINE_TIMEOUT,
  offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT,
  /** 抓取驱动三态；有则优先 */
  presenceById = null,
  /** 下达坐标：地图种水滴后单独发给该队员 */
  onSendLocationToMember,
  /** 下达消息：打开与地图图标相同的指挥对话 */
  onSendMessageToMember,
  /** 当前指挥员私有备注 map: memberKey -> note */
  notesByKey = null,
  /** 保存备注 */
  onSaveMemberNote,
  /** 全部页：勾选加入任务中 */
  missionSelectable = false,
  missionPickIds = null,
  onMissionPickToggle,
  onMissionPickMany,
  missionMemberIdSet = null
}) {
  const missionPickEnabled = missionSelectable && canEditMissionBoard();
  const canEditNotes = hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_NOTES);
  // 定时刷新，使状态灯在超时后尽快切换（不必等下次设备列表轮询）
  const [, setOnlineTick] = useState(0);
  /** { key, member, top, left } — Portal 固定定位，避免侧栏 overflow 裁切 */
  const [commandMenu, setCommandMenu] = useState(null);
  const [noteEditor, setNoteEditor] = useState(null);
  const commandMenuRef = useRef(null);
  const nameAnchorRefs = useRef({});

  const pickSet = useMemo(() => {
    if (missionPickIds instanceof Set) return missionPickIds;
    return new Set(Array.isArray(missionPickIds) ? missionPickIds : []);
  }, [missionPickIds]);
  const inMissionSet = useMemo(() => {
    if (missionMemberIdSet instanceof Set) return missionMemberIdSet;
    return new Set(Array.isArray(missionMemberIdSet) ? missionMemberIdSet : []);
  }, [missionMemberIdSet]);

  const collectIds = (list = []) => (list || []).map((m) => getMemberExpandKey(m)).filter(Boolean);


  const closeCommandMenu = () => setCommandMenu(null);

  const openCommandMenuFor = (member, anchorEl) => {
    const key = member.device_unique_id || member.name;
    const rect = (anchorEl || nameAnchorRefs.current[key])?.getBoundingClientRect?.();
    if (!rect) return;
    setCommandMenu((prev) => {
      if (prev?.key === key) return null;
      const estimatedH = 88;
      const top = rect.bottom + 4 + estimatedH > window.innerHeight
        ? Math.max(8, rect.top - estimatedH - 4)
        : rect.bottom + 4;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - 140);
      return { key, member, top, left };
    });
  };

  // 下一帧再监听外部点击，避免「打开菜单的同一次点击」立刻关掉
  useEffect(() => {
    if (!commandMenu) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      if (!active) return;
      const onPointerDown = (e) => {
        if (commandMenuRef.current?.contains?.(e.target)) return;
        const key = commandMenu.key;
        const anchor = nameAnchorRefs.current[key];
        if (anchor?.contains?.(e.target)) return;
        closeCommandMenu();
      };
      const onKey = (e) => { if (e.key === 'Escape') closeCommandMenu(); };
      document.addEventListener('mousedown', onPointerDown, true);
      document.addEventListener('keydown', onKey);
      commandMenuRef._onPointerDown = onPointerDown;
      commandMenuRef._onKey = onKey;
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
      if (commandMenuRef._onPointerDown) {
        document.removeEventListener('mousedown', commandMenuRef._onPointerDown, true);
      }
      if (commandMenuRef._onKey) {
        document.removeEventListener('keydown', commandMenuRef._onKey);
      }
    };
  }, [commandMenu]);

  useLayoutEffect(() => {
    if (!commandMenu) return undefined;
    const onScrollOrResize = () => closeCommandMenu();
    window.addEventListener('resize', onScrollOrResize);
    // 侧栏滚动时关闭，避免锚点错位
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [commandMenu]);

  useEffect(() => {
    const timer = setInterval(() => setOnlineTick((n) => n + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  const isDrawing = (name) => (realTimeTrackDrawing?.has ? realTimeTrackDrawing.has(name) : false);
  const t = THEMES[variant] || THEMES.desktop;
  const isStreaming = (name) => (streamingMembers?.has ? streamingMembers.has(name) : false);
  const isHiddenOnMap = (member) => {
    const id = member?.device_unique_id || member?.name;
    return !!(id && hiddenMapMemberIds?.has?.(id));
  };

  const toggleMemberExpand = (member, memberKey, isExpanded) => {
    const newExpanded = !isExpanded;
    const memberId = member.device_unique_id || member.name;
    setOpenMember((prev) => ({ ...prev, [memberKey]: newExpanded }));
    if (newExpanded) {
      setHighlightedMemberIds((prev) => new Set([...prev, memberId]));
    } else {
      setHighlightedMemberIds((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
    }
  };

  const commandMenuPortal = commandMenu && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
      (
        <div
          ref={commandMenuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: commandMenu.top,
            left: commandMenu.left,
            zIndex: 10050,
            minWidth: 120,
            background: '#fff',
            border: '1px solid #d0d7de',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15,23,42,.18)',
            padding: 6
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendLocationToMember?.(m);
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              background: '#f8fafc',
              padding: '8px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: '#0f172a',
              marginBottom: 4
            }}
          >
            发送位置
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendMessageToMember?.(m);
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              background: '#f8fafc',
              padding: '8px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: '#0f172a',
              marginBottom: 4
            }}
          >
            发送消息
          </button>
          {canEditNotes && (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              setNoteEditor({ member: m, focusSection: 'remark' });
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              background: '#f8fafc',
              padding: '8px 10px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: '#0f172a'
            }}
          >
            备注
          </button>
          )}
        </div>
      ),
      document.body
    )
    : null;

  return (
    <>
    <div style={t.root}>
      {Object.entries(orgMap).map(([org, groups]) => (
        <div key={org} style={t.orgWrap}>
          <div style={t.orgHeader} onClick={() => setOpenOrg((prev) => ({ ...prev, [org]: !prev[org] }))}>
            {t.orgChevronSide === 'left' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={t.orgChevron}>{openOrg[org] ? '▼' : '▶'}</span>
                {missionPickEnabled ? (
                  <input
                    type="checkbox"
                    checked={(() => {
                      const ids = collectIds(Object.values(groups || {}).flat());
                      return ids.length > 0 && ids.every((id) => pickSet.has(id) || inMissionSet.has(id));
                    })()}
                    ref={(el) => {
                      if (!el) return;
                      const ids = collectIds(Object.values(groups || {}).flat());
                      const some = ids.some((id) => pickSet.has(id) || inMissionSet.has(id));
                      const all = ids.length > 0 && ids.every((id) => pickSet.has(id) || inMissionSet.has(id));
                      el.indeterminate = some && !all;
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      const ids = collectIds(Object.values(groups || {}).flat()).filter((id) => !inMissionSet.has(id));
                      onMissionPickMany?.(ids, e.target.checked);
                    }}
                    title="勾选本组织加入任务"
                  />
                ) : null}
                <span style={t.orgTitle}>{org}</span>
              </div>
            ) : (
              <span style={t.orgTitle}>{org}</span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenOrg((prev) => ({ ...prev, [org]: true }));
                  expandOnlineInOrg?.(groups);
                }}
                style={{ ...btnGreen, ...t.orgBtn }}
              >
                展开在线
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const expand = !areAllGroupsExpanded?.(groups);
                  setOpenOrg((prev) => ({ ...prev, [org]: true }));
                  setOrgGroupsExpanded?.(groups, expand);
                }}
                style={{ ...btnBlue, ...t.orgBtn }}
              >
                {areAllGroupsExpanded?.(groups) ? '全部收起' : '全部展开'}
              </button>
              {t.orgChevronSide === 'right' && <span>{openOrg[org] ? '▼' : '▶'}</span>}
            </div>
          </div>

          {openOrg[org] && (
            <div style={t.groupsWrap}>
              {Object.entries(groups).map(([group, members]) => (
                <div key={group} style={t.groupWrap}>
                  <div style={t.groupHeader} onClick={() => setOpenGroup((prev) => ({ ...prev, [group]: !prev[group] }))}>
                    {t.orgChevronSide === 'left' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={t.groupChevron}>{openGroup[group] ? '▼' : '▶'}</span>
                        {missionPickEnabled ? (
                          <input
                            type="checkbox"
                            checked={(() => {
                              const ids = collectIds(members);
                              return ids.length > 0 && ids.every((id) => pickSet.has(id) || inMissionSet.has(id));
                            })()}
                            ref={(el) => {
                              if (!el) return;
                              const ids = collectIds(members);
                              const some = ids.some((id) => pickSet.has(id) || inMissionSet.has(id));
                              const all = ids.length > 0 && ids.every((id) => pickSet.has(id) || inMissionSet.has(id));
                              el.indeterminate = some && !all;
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              const ids = collectIds(members).filter((id) => !inMissionSet.has(id));
                              onMissionPickMany?.(ids, e.target.checked);
                            }}
                            title="勾选本小组加入任务"
                          />
                        ) : null}
                        <span style={t.groupTitle}>{group}</span>
                      </div>
                    ) : (
                      <span style={t.groupTitle}>{group}</span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenGroup((prev) => ({ ...prev, [group]: true }));
                          expandOnlineInGroup?.(members);
                        }}
                        style={{ ...btnGreen, ...t.groupBtn }}
                      >
                        展开在线
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const expand = !areAllMembersExpanded?.(members);
                          setOpenGroup((prev) => ({ ...prev, [group]: true }));
                          setGroupMembersExpanded?.(members, expand);
                        }}
                        style={{ ...btnBlue, ...t.groupBtn }}
                      >
                        {areAllMembersExpanded?.(members) ? '全部收起' : '全部展开'}
                      </button>
                      {t.orgChevronSide === 'right' && <span>{openGroup[group] ? '▼' : '▶'}</span>}
                    </div>
                  </div>

                  {openGroup[group] && (
                    <div style={t.membersWrap}>
                      {members.map((member) => (
                        <div
                          key={member.device_unique_id || member.name}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}
                        >
                        {missionPickEnabled ? (
                          <input
                            type="checkbox"
                            style={{ marginTop: 10, flexShrink: 0 }}
                            checked={pickSet.has(getMemberExpandKey(member)) || inMissionSet.has(getMemberExpandKey(member))}
                            disabled={inMissionSet.has(getMemberExpandKey(member))}
                            onChange={() => onMissionPickToggle?.(getMemberExpandKey(member))}
                            title={inMissionSet.has(getMemberExpandKey(member)) ? '已在任务中' : '勾选加入任务'}
                          />
                        ) : null}
                        <div style={{ flex: 1, minWidth: 0 }}>
                        <MemberTreeRow
                          member={member}
                          theme={t}
                          openMember={openMember}
                          highlightedMemberName={highlightedMemberName}
                          showTracks={showTracks}
                          isMemberTrackVisible={isMemberTrackVisible}
                          presenceById={presenceById}
                          onlineTimeoutMs={onlineTimeoutMs}
                          offlineTimeoutMs={offlineTimeoutMs}
                          nameAnchorRefs={nameAnchorRefs}
                          isStreaming={isStreaming}
                          isHiddenOnMap={isHiddenOnMap}
                          isDrawing={isDrawing}
                          toggleMemberExpand={toggleMemberExpand}
                          openCommandMenuFor={openCommandMenuFor}
                          flyToMember={flyToMember}
                          setActiveMemberId={setActiveMemberId}
                          setHighlightedMemberName={setHighlightedMemberName}
                          toggleTrack={toggleTrack}
                          openLiveStream={openLiveStream}
                          onToggleMemberMapHidden={onToggleMemberMapHidden}
                          startRealTimeTrackDrawing={startRealTimeTrackDrawing}
                          stopTrackFeatures={stopTrackFeatures}
                          copyToClipboard={copyToClipboard}
                          openDownloadTrackModal={openDownloadTrackModal}
                          fetchMemberTrack={fetchMemberTrack}
                          onMemberIconChange={onMemberIconChange}
                          commandFeatures={commandFeatures}
                          notesByKey={notesByKey}
                        />
                        </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
    {commandMenuPortal}
    <MemberNoteEditorModal
      open={!!noteEditor}
      member={noteEditor?.member}
      note={
        noteEditor?.member
          ? (notesByKey && notesByKey[memberNoteKey(noteEditor.member)]) || null
          : null
      }
      focusSection={noteEditor?.focusSection || 'remark'}
      onClose={() => setNoteEditor(null)}
      onSave={onSaveMemberNote}
    />
    </>
  );
}
